<template>
  <div class="atb-divide-y atb-divide-gray-300 atb-py-6">
    <Card class="card-size atb-ml-20 atb-pt-6">
      <template #body>
        <Form>
          <div
            class="atb-grid atb-grid-cols-2 atb-gap-y-4 atb-gap-x-14 atb-px-2 atb-py-4"
          >
            <div
              :class="{
                'atb-flex atb-items-center': clientNameErrorMsg.length,
              }"
            >
              <FormField
                :label="$t('client')"
                required="true"
                form-field-class="input-field-width"
                :message="clientNameErrorMsg"
                :message-type="'error'"
              >
                <Textbox
                  id="clientTextbox"
                  v-model.trim="clientDataValue.clientName"
                  type="text"
                  :maxlength="MaxValue.MAX_SHORT_NAME"
                  :state="clientNameErrorMsg ? 'error' : 'basic'"
                  @update:modelValue="validateClientName"
                />
              </FormField>
            </div>
          </div>
          <div
            class="atb-grid atb-grid-cols-2 atb-gap-y-6 atb-gap-x-16 atb-px-2 atb-py-4"
          >
            <div
              class="atb-col-span-2"
              :class="{
                'atb-flex atb-items-center': clientDescriptionErrorMsg.length,
              }"
            >
              <FormField
                :label="$t('Description')"
                form-field-class="input-field-width"
                :message="clientDescriptionErrorMsg"
                :message-type="'error'"
              >
                <div class="atb-flex atb-flex-col atb-gap-4">
                  <Textarea
                    id="attributesDetailsCodeRevisionCommentTextboxModal"
                    v-model="clientDataValue.description"
                    field-control-class="atb-h-24"
                  />
                </div>
              </FormField>
            </div>
          </div>
        </Form>
        <div class="datasources-table atb-m-4 atb-h-100">
          <div
            class="atb-font-bold atb-px-4 atb-py-4 atb-flex atb-align-center"
          >
            {{ $t('dataSources') }}
          </div>
          <div class="grid-top-border">
            <AgGridVue
              :style="{ height: '350px' }"
              class="ag-theme-eds atb-w-full atb-h-full"
              :column-defs="dsColumnDefs"
              :row-data="dsRowData"
              :row-selection="'multiple'"
              :suppress-row-click-selection="true"
              :pagination="false"
              :row-multi-select-with-click="true"
              @grid-ready="onDatasourceGridReady"
              @row-selected="onDataSourceRowSelected"
            />
          </div>
        </div>
        <div class="atb-flex atb-flex-row atb-justify-end atb-gap-x-8 atb-pt-8">
          <Button
            id="overviewTabDeleteButton"
            type="submit"
            motif="basic"
            :label="$t('delete')"
            :title="$t('delete')"
            :disabled="readOnly || isDeleteDisabled"
            @click="deleteClient"
          />
          <Button
            id="overviewTabSaveButton"
            type="submit"
            motif="primary"
            :label="$t('save')"
            :title="$t('save')"
            :disabled="readOnly"
            @click="saveClientwDetails"
          />
        </div>
      </template>
    </Card>
  </div>
  <Modal
    :show="isSaveConfirmationDisplayed"
    container-class="atb-w-96"
    body-class="atb-border-none"
    footer-class="atb-flex atb-flex-row atb-justify-end atb-gap-8"
    @onModalClose="isSaveConfirmationDisplayed = false"
  >
    <template #header>{{ $t('areYouSure') }}</template>
    <template #body>
      <div
        id="overviewTabSaveConfirmationModal"
        class="atb-flex atb-flex-col atb-gap-4"
      >
        {{ $t('checkingClientSave') }}
      </div>
    </template>
    <template #footer>
      <Button
        motif="basic"
        @click="isSaveConfirmationDisplayed = false"
        >{{ $t('cancel') }}</Button
      >
      <Button
        id="overviewTabSaveConfirmationButton"
        @click="saveConfirmation"
        >{{ $t('ok') }}</Button
      >
    </template>
  </Modal>
  <Modal
    :show="isSaveErrorDisplayed"
    container-class="atb-w-96"
    body-class="atb-border-none"
    footer-class="atb-flex atb-flex-row atb-justify-end atb-gap-8"
    @onModalClose="isSaveErrorDisplayed = false"
  >
    <template #header>{{ $t('oops') }}</template>
    <template #body>
      <div class="atb-flex atb-flex-col atb-gap-4">
        {{ saveError }}
      </div>
    </template>
    <template #footer>
      <Button @click="isSaveErrorDisplayed = false">{{ $t('ok') }}</Button>
    </template>
  </Modal>
</template>

<script setup lang="ts">
  import { MaxValue } from '@/app/shared/enums/max-value.enum';
  import { Motif } from '@/app/shared/enums/motif-enum';
  import { useAlertStore } from '@/app/shared/stores/alert-store';
  import {
    validateAlphanumeric,
    validateEmpty,
  } from '@/app/shared/utils/validation.util';
  import { AgGridVue } from 'ag-grid-vue3/lib/AgGridVue';

  import { ENTITY_ALPHANUMERIC_REGEX } from '@/app/utils/regexpr-const';
  import Button from '@atb-ui/eds-vue/src/components/Button.vue';
  import Card from '@atb-ui/eds-vue/src/components/Card.vue';
  import Form from '@atb-ui/eds-vue/src/components/Form/Form.vue';
  import FormField from '@atb-ui/eds-vue/src/components/Form/FormField.vue';
  import Modal from '@atb-ui/eds-vue/src/components/Modal.vue';
  import Textbox from '@atb-ui/eds-vue/src/components/Textbox.vue';
  import Textarea from '@atb-ui/eds-vue/src/components/Textarea.vue';
  import { AgGridEvent, GridApi } from 'ag-grid-community';
  import { computed, Ref, ref, toRefs, watch, onMounted, nextTick } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { ClientDataEvent } from '../client-data-event.interface';
  import { Datasource } from '@/app/shared/types/datasource.interface';
  import { useClientStore } from '@/app/features/admin/clients/client-store';

  interface NewClientTabEvent {
    (_event: 'clientMetaInformationsDeleted'): void;
    (_event: 'saveClientData', _params: ClientDataEvent): void;
    (
      _event: 'setDirtyStatus',
      stringField: string,
      booleanField: boolean
    ): void;
    (_event: 'deleteClientData', _params: number | string): void;
  }

  interface ClientTabProps {
    clientId: number;
    readOnly: boolean;
    useDataSource?: boolean;
    clientData: ClientDataEvent;
  }

  const emit = defineEmits<NewClientTabEvent>();
  const props = withDefaults(defineProps<ClientTabProps>(), {
    useDataSource: false,
  });
  const { t } = useI18n();
  const clientStore = useClientStore();
  const alertStore = useAlertStore();

  const { clientId, readOnly, clientData: clientDataProp } = toRefs(props);

  const clientDataValue = clientDataProp.value;

  const dsGridApi = ref(null) as Ref<GridApi | null>;

  const selectedDataSources: Ref<any[]> = ref([]);

  const isSaveConfirmationDisplayed = ref(false);
  const isSaveErrorDisplayed = ref(false);
  const clientNameErrorMsg = ref('');
  const clientDescriptionErrorMsg = ref('');
  const saveError = ref(t('unknownError'));

  const initialClientName = ref('');

  onMounted(async () => {
    await clientStore.getDataSourceList();
    initialClientName.value = clientDataValue.clientName;
  });

  const dsRowData = computed(() => {
    return clientStore.dataSourceList;
  });

  watch(
    () => dsRowData.value,
    async () => {
      if (dsGridApi.value) {
        await nextTick();
        updateSelectedElements();
      }
    }
  );

  const dsColumnDefs = ref([
    {
      field: 'check',
      headerName: '',
      width: 56,
      suppressSizeToFit: true,
      headerCheckboxSelection: true,
      checkboxSelection: true,
    },
    {
      field: 'name',
      headerName: t('dataSourceHistory'),
      suppressSizeToFit: true,
      sortable: true,
    },
    {
      field: 'description',
      headerName: 'Description',
      suppressSizeToFit: false,
    },
  ]);

  const onDatasourceGridReady = async (params: AgGridEvent) => {
    dsGridApi.value = params.api;
    dsGridApi.value.setRowData(dsRowData.value);

    await nextTick();
    updateSelectedElements();
    dsGridApi.value.sizeColumnsToFit();
  };

  const updateSelectedElements = () => {
    if (!dsGridApi.value) return;

    const selectedSystemsIds =
      clientDataValue.dataSources?.map((elem: Datasource) => elem.id) || [];

    dsGridApi.value.forEachNode((node) => {
      node.setSelected(selectedSystemsIds.includes(node.data.id));
    });
  };

  const onDataSourceRowSelected = () => {
    const selectedData = dsGridApi.value?.getSelectedNodes();
    selectedDataSources.value =
      selectedData?.map((node) => node.data.name) || [];
  };

  const isDeleteDisabled = computed((): boolean => {
    return clientId.value === -1;
  });

  const setDirtyField = (isDirty: boolean) => {
    emit('setDirtyStatus', t('overview'), isDirty);
  };

  const validateClientName = () => {
    clientNameErrorMsg.value = '';
  };

  const validateForm = (): boolean => {
    clientNameErrorMsg.value = '';
    clientDescriptionErrorMsg.value = '';

    let hasError = false;

    const { clientName, description } = clientDataValue;

    if (!clientName || validateEmpty(clientName)) {
      clientNameErrorMsg.value = t('emptyField');
      hasError = true;
    } else if (!validateAlphanumeric(ENTITY_ALPHANUMERIC_REGEX, clientName)) {
      clientNameErrorMsg.value = t('invalidClientName');
      hasError = true;
    } else if (clientName.length > MaxValue.MAX_SHORT_NAME) {
      clientNameErrorMsg.value = t('clientNameTooLong');
      hasError = true;
    }

    if (description && description.length > 500) {
      clientDescriptionErrorMsg.value = t('descriptionTooLong');
      hasError = true;
    }

    if (hasError) {
      alertStore.showNotification(
        t('pleaseFixValidationErrors'),
        t('client'),
        Motif.ERROR
      );
    }

    return !hasError;
  };

  const saveClientwDetails = async () => {
    if (!validateForm()) {
      return;
    }
    isSaveConfirmationDisplayed.value = true;
  };

  const saveClientData = async () => {
    const selectedDataSourceList: Datasource[] | undefined =
      dsGridApi.value?.getSelectedRows();
    clientDataValue.dataSources = selectedDataSourceList || [];
    setDirtyField(false);
    emit('saveClientData', clientDataValue);
    alertStore.showNotification(
      t('clientSavedSuccessfully'),
      t('client'),
      Motif.SUCCESS
    );
  };

  const saveConfirmation = () => {
    isSaveConfirmationDisplayed.value = false;
    saveClientData();
  };

  const deleteClient = () => {
    const clientId = clientDataValue.id;
    if (clientId != null) {
      emit('deleteClientData', clientId);
    } else {
      console.error('Cannot delete: client ID is missing.');
    }
  };
</script>

<style scoped>
  .card-size {
    width: 70%;
  }

  .input-field-width {
    width: 90%;
  }

  .option-class {
    text-align: left;
    padding: 10px 16px;
  }

  .d-active {
    background-color: #426da9;
    color: white;
  }

  .card-title {
    font-weight: 500;
    font-size: 16px;
    color: #767676;
  }
  .tools-header-tab {
    align-items: center;
    height: 50px;
  }
  .datasources-table {
    border: 1px solid #d6d6d6;
    border-radius: 6px;
  }
  .grid-top-border {
    border-top: 1px solid #d6d6d6;
  }
</style>
